import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { HelpDrawer } from '../../components/HelpDrawer';
import { PLANS } from '../../data/planConfig';
import {
  CreditCard, Zap, CheckCircle, XCircle, RefreshCw, Plus, Trash2,
  Building2, ExternalLink, Tag, Users, Settings, AlertTriangle,
  Percent, DollarSign, Gift, Clock, Shield, TrendingUp, Lock, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const EMPTY_COUPON = { code: '', discount_type: 'percent', discount_value: 10, max_redemptions: '', expires_at: '', applies_to_plan: '' };
const EMPTY_CONNECT = { restaurant_id: '', restaurant_name: '', email: '', country: 'MX' };

function StatusPill({ configured, loading }) {
  if (loading) return <Badge className="bg-gray-100 text-gray-500 gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Verificando...</Badge>;
  return configured
    ? <Badge className="bg-emerald-100 text-emerald-700 gap-1 text-xs"><CheckCircle className="w-3 h-3" /> Conectado</Badge>
    : <Badge className="bg-red-100 text-red-700 gap-1 text-xs"><XCircle className="w-3 h-3" /> No configurado</Badge>;
}

const PLAN_COLOR = { gratuito: 'gray', basico: 'blue', profesional: 'orange', empresarial: 'black' };

export default function AdminStripe() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [connectAccounts, setConnectAccounts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [couponForm, setCouponForm] = useState(EMPTY_COUPON);
  const [connectForm, setConnectForm] = useState(EMPTY_CONNECT);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/stripe/status`, { headers: authHeader() });
      if (res.ok) setStatus(await res.json());
    } catch { }
    finally { setLoadingStatus(false); }
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stripe/plans`, { headers: authHeader() });
      if (res.ok) setPlans(await res.json());
    } catch { }
  }, []);

  const loadCoupons = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stripe/coupons`, { headers: authHeader() });
      if (res.ok) setCoupons(await res.json());
    } catch { }
  }, []);

  const loadConnect = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stripe/connect/accounts`, { headers: authHeader() });
      if (res.ok) setConnectAccounts(await res.json());
    } catch { }
  }, []);

  const loadAssignments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stripe/plans/assignments`, { headers: authHeader() });
      if (res.ok) setAssignments(await res.json());
    } catch { }
  }, []);

  useEffect(() => {
    loadStatus();
    loadPlans();
    loadCoupons();
    loadConnect();
    loadAssignments();
  }, [loadStatus, loadPlans, loadCoupons, loadConnect, loadAssignments]);

  const syncPlans = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/stripe/plans/sync`, { method: 'POST', headers: authHeader() });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.synced?.length || 0} planes sincronizados con Stripe`);
        loadPlans();
      } else {
        toast.error(data.detail || 'Error al sincronizar');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setSyncing(false); }
  };

  const saveCoupon = async () => {
    if (!couponForm.code || !couponForm.discount_value) { toast.error('Código y descuento son obligatorios'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/stripe/coupons`, { method: 'POST', headers: authHeader(), body: JSON.stringify({ ...couponForm, discount_value: Number(couponForm.discount_value) }) });
      const data = await res.json();
      if (res.ok) {
        toast.success('Cupón creado' + (data.demo_mode ? ' (modo demo)' : ''));
        setShowCouponModal(false);
        setCouponForm(EMPTY_COUPON);
        loadCoupons();
      } else toast.error(data.detail || 'Error');
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm(`¿Eliminar cupón "${id}"?`)) return;
    await fetch(`${API_URL}/api/admin/stripe/coupons/${id}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Cupón eliminado'); loadCoupons();
  };

  const createConnect = async () => {
    if (!connectForm.restaurant_id || !connectForm.email) { toast.error('ID de restaurante y email son requeridos'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/stripe/connect/create`, { method: 'POST', headers: authHeader(), body: JSON.stringify(connectForm) });
      const data = await res.json();
      if (res.ok) {
        if (data.onboarding_url) {
          window.open(data.onboarding_url, '_blank');
          toast.success('Redirigiendo al onboarding de Stripe Connect');
        } else {
          toast.success('Cuenta Connect creada' + (data.demo_mode ? ' (modo demo)' : ''));
        }
        setShowConnectModal(false);
        setConnectForm(EMPTY_CONNECT);
        loadConnect();
      } else toast.error(data.detail || 'Error');
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const removeConnect = async (rid) => {
    if (!window.confirm('¿Desconectar esta cuenta?')) return;
    await fetch(`${API_URL}/api/admin/stripe/connect/accounts/${rid}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Cuenta desconectada'); loadConnect();
  };

  const configured = status?.configured;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black text-gray-900">Stripe — Pagos y Planes</h1>
            <HelpDrawer section="integrations" compact />
          </div>
          <p className="text-gray-500 mt-1">Gestiona suscripciones, Stripe Connect y cupones de descuento</p>
        </div>
        <StatusPill configured={configured} loading={loadingStatus} />
      </div>

      {/* Setup Alert */}
      {!configured && !loadingStatus && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5 flex gap-4 items-start">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-800">Stripe no configurado</p>
            <p className="text-amber-700 text-sm mt-1">Agrega tu <code className="bg-amber-100 px-1 rounded">STRIPE_SECRET_KEY</code> y <code className="bg-amber-100 px-1 rounded">STRIPE_PUBLISHABLE_KEY</code> en el panel de Secrets del proyecto. Puedes usar claves de prueba para comenzar.</p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white gap-1" onClick={() => window.open('https://dashboard.stripe.com/apikeys', '_blank')}>
                <ExternalLink className="w-3 h-3" /> Obtener claves en Stripe
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Plan activos', value: status?.active_subscriptions ?? 0, icon: CreditCard, color: 'text-blue-500' },
          { label: 'Cuentas Connect', value: status?.connected_accounts ?? 0, icon: Building2, color: 'text-orange-500' },
          { label: 'Fee de plataforma', value: `${status?.connect_fee_percent ?? 2}%`, icon: Percent, color: 'text-emerald-500' },
          { label: 'Planes configurados', value: status?.plans_count ?? 4, icon: TrendingUp, color: 'text-purple-500' },
        ].map((s, i) => (
          <Card key={i}><CardContent className="p-4 flex items-center gap-3">
            <s.icon className={`w-8 h-8 ${s.color}`} />
            <div><p className="text-2xl font-black">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
          </CardContent></Card>
        ))}
      </div>

      <Tabs defaultValue="plans">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="plans" className="gap-1.5"><CreditCard className="w-4 h-4" />Planes y Suscripciones</TabsTrigger>
          <TabsTrigger value="connect" className="gap-1.5"><Building2 className="w-4 h-4" />Stripe Connect</TabsTrigger>
          <TabsTrigger value="coupons" className="gap-1.5"><Tag className="w-4 h-4" />Cupones</TabsTrigger>
          <TabsTrigger value="assignments" className="gap-1.5"><Users className="w-4 h-4" />Asignaciones</TabsTrigger>
        </TabsList>

        {/* ── PLANES ── */}
        <TabsContent value="plans" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-black text-lg text-gray-900">Planes de Suscripción</h2>
              <p className="text-sm text-gray-500">Los planes se sincronizan como Productos + Precios en tu cuenta de Stripe</p>
            </div>
            <Button onClick={syncPlans} disabled={syncing || !configured} className="gap-2 bg-orange-500 hover:bg-orange-600 text-white">
              {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Sincronizar con Stripe
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {PLANS.map(plan => {
              const serverPlan = plans.find(p => p.id === plan.id);
              return (
                <Card key={plan.id} className={`relative overflow-hidden border-2 ${plan.cardBg}`}>
                  {plan.badge && (
                    <div className="absolute top-0 right-0 text-xs font-bold px-3 py-1 bg-orange-500 text-white rounded-bl-xl">
                      {plan.badge}
                    </div>
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-xs font-bold bg-gradient-to-r ${plan.color} mb-2`}>
                          {plan.id === 'gratuito' && <Gift className="w-3 h-3" />}
                          {plan.id === 'basico' && <CreditCard className="w-3 h-3" />}
                          {plan.id === 'profesional' && <Zap className="w-3 h-3" />}
                          {plan.id === 'empresarial' && <Shield className="w-3 h-3" />}
                          {plan.name}
                        </div>
                        <p className="text-2xl font-black text-gray-900">
                          {plan.price_mxn === 0 ? 'Gratis' : `$${plan.price_mxn.toLocaleString()} MXN`}
                          {plan.price_mxn > 0 && <span className="text-sm font-normal text-gray-400">/mes</span>}
                        </p>
                        <p className="text-xs text-gray-400">{plan.price_usd > 0 ? `~$${plan.price_usd} USD/mes` : 'Sin costo'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-black text-gray-700">{serverPlan?.active_subscribers ?? 0}</p>
                        <p className="text-xs text-gray-400">restaurantes</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 italic mb-3">{plan.tagline}</p>
                    <div className="space-y-1.5">
                      {plan.features.slice(0, 5).map((f, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-gray-700">{f}</p>
                        </div>
                      ))}
                      {plan.features.length > 5 && <p className="text-xs text-gray-400 pl-5">+{plan.features.length - 5} más...</p>}
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Stripe Price ID:</span>
                        <code className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                          {plan.stripe_price_id || 'N/A (gratuito)'}
                        </code>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Feature Gating Info */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-5">
              <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2"><Lock className="w-4 h-4" /> Control de Acceso por Plan</h3>
              <p className="text-sm text-blue-700 mb-3">Las secciones del sistema se habilitan automáticamente según el plan del restaurante:</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { plan: 'Gratuito', sections: ['Dashboard', 'Libro Digital', 'Reservaciones', 'Lista de Espera', 'Clientes', 'Página Pública'], color: 'bg-gray-100 text-gray-700' },
                  { plan: 'Básico', sections: ['Plano de Sala', 'Depósitos', 'WhatsApp', 'Fidelización', 'Tarjetas Regalo', 'Analytics', 'Walk-In'], color: 'bg-blue-100 text-blue-700' },
                  { plan: 'Profesional', sections: ['Eventos', 'Comedores Privados', 'Menú Alérgenos', 'CFDI', 'Marketing', 'NPS', 'No-Shows', 'Benchmark'], color: 'bg-orange-100 text-orange-700' },
                  { plan: 'Empresarial', sections: ['Portal Corporativo', 'Staff Scheduling', 'Acceso Garantizado', 'Integraciones Avanzadas'], color: 'bg-gray-900/10 text-gray-800' },
                ].map(({ plan, sections, color }, i) => (
                  <div key={i}>
                    <Badge className={`${color} mb-2 text-xs`}>{plan}</Badge>
                    {sections.map((s, j) => (
                      <p key={j} className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                        <CheckCircle className="w-2.5 h-2.5 text-emerald-500" /> {s}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── STRIPE CONNECT ── */}
        <TabsContent value="connect" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-black text-lg text-gray-900">Stripe Connect — Modalidad de Plataforma</h2>
              <p className="text-sm text-gray-500">Los restaurantes cobran a través de tu cuenta plataforma. Tú retenes el fee automáticamente en cada transacción.</p>
            </div>
            <Button onClick={() => setShowConnectModal(true)} className="bg-orange-500 hover:bg-orange-600 text-white gap-2">
              <Plus className="w-4 h-4" /> Conectar restaurante
            </Button>
          </div>

          {/* How Connect Works */}
          <Card className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
            <CardContent className="p-6">
              <h3 className="font-black text-lg mb-4 flex items-center gap-2"><Globe className="w-5 h-5" /> ¿Cómo funciona Stripe Connect?</h3>
              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  { n: '1', title: 'Restaurante se conecta', desc: 'El restaurante completa el onboarding de Stripe Express a través de OkReserva.' },
                  { n: '2', title: 'Cliente hace un pago', desc: 'El cliente paga un depósito, evento o membresía. El dinero llega a la cuenta del restaurante.' },
                  { n: '3', title: 'OkReserva retiene el fee', desc: `Automáticamente se descuenta el ${status?.connect_fee_percent ?? 2}% de cada transacción como fee de plataforma.` },
                ].map(step => (
                  <div key={step.n} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 font-black flex items-center justify-center flex-shrink-0">{step.n}</div>
                    <div><p className="font-bold text-sm">{step.title}</p><p className="text-white/70 text-xs mt-1">{step.desc}</p></div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/20 flex items-center gap-4">
                <div><p className="text-xs text-white/60">Fee actual de plataforma</p><p className="text-2xl font-black">{status?.connect_fee_percent ?? 2}%</p></div>
                <div className="flex-1 text-xs text-white/60">Configurable por restaurante. El monto se transfiere automáticamente a tu cuenta principal de Stripe en cada pago.</div>
              </div>
            </CardContent>
          </Card>

          {/* Accounts list */}
          {connectAccounts.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No hay restaurantes conectados vía Stripe Connect</p>
              <p className="text-sm">Conecta el primer restaurante para empezar a procesar pagos con fee de plataforma</p>
            </div>
          ) : (
            <div className="space-y-3">
              {connectAccounts.map(acct => (
                <Card key={acct.restaurant_id}><CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-black">
                      {acct.restaurant_name?.charAt(0) || 'R'}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{acct.restaurant_name}</p>
                      <p className="text-xs text-gray-400">{acct.email} · <code className="bg-gray-100 px-1 rounded">{acct.account_id}</code></p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={acct.status === 'active' ? 'bg-emerald-100 text-emerald-700' : acct.status === 'demo' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}>
                      {acct.status === 'pending_onboarding' ? 'Pendiente onboarding' : acct.status === 'demo' ? 'Demo' : 'Activo'}
                    </Badge>
                    {acct.onboarding_url && (
                      <Button size="sm" variant="outline" onClick={() => window.open(acct.onboarding_url, '_blank')} className="gap-1">
                        <ExternalLink className="w-3 h-3" /> Onboarding
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeConnect(acct.restaurant_id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent></Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── CUPONES ── */}
        <TabsContent value="coupons" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-black text-lg text-gray-900">Cupones de Descuento</h2>
              <p className="text-sm text-gray-500">Crea códigos de descuento para planes con porcentaje, monto fijo, límite de usos y periodos de prueba.</p>
            </div>
            <Button onClick={() => setShowCouponModal(true)} className="bg-orange-500 hover:bg-orange-600 text-white gap-2">
              <Plus className="w-4 h-4" /> Nuevo cupón
            </Button>
          </div>

          {coupons.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Tag className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No hay cupones activos todavía</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {coupons.map(c => (
                <Card key={c.id} className="relative border-dashed border-2 border-gray-200 overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 to-orange-600" />
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <code className="font-black text-lg text-gray-900 bg-orange-50 px-3 py-1 rounded-lg border border-orange-200">{c.id}</code>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteCoupon(c.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center gap-2">
                        {c.percent_off ? <Percent className="w-3.5 h-3.5 text-orange-500" /> : <DollarSign className="w-3.5 h-3.5 text-orange-500" />}
                        <span className="font-bold text-orange-600">
                          {c.percent_off ? `${c.percent_off}% de descuento` : `$${(c.amount_off / 100).toFixed(0)} MXN de descuento`}
                        </span>
                      </div>
                      {c.max_redemptions && (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Gift className="w-3.5 h-3.5" />
                          <span>{c.times_redeemed || 0} / {c.max_redemptions} usos</span>
                        </div>
                      )}
                      <Badge className={c.valid !== false ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-gray-100 text-gray-500 text-xs'}>
                        {c.valid !== false ? 'Activo' : 'Expirado'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── ASIGNACIONES ── */}
        <TabsContent value="assignments" className="mt-6 space-y-4">
          <div>
            <h2 className="font-black text-lg text-gray-900">Asignaciones de Plan por Restaurante</h2>
            <p className="text-sm text-gray-500">Vista de qué plan tiene asignado cada restaurante.</p>
          </div>
          {assignments.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aún no hay asignaciones manuales. Los restaurantes nuevos reciben automáticamente el plan Gratuito.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {assignments.map(a => {
                const plan = PLANS.find(p => p.id === a.plan_id);
                return (
                  <Card key={a.restaurant_id}><CardContent className="p-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <code className="text-sm text-gray-700">{a.restaurant_id}</code>
                    </div>
                    <Badge className={`bg-gradient-to-r ${plan?.color} text-white text-xs`}>{plan?.name || a.plan_id}</Badge>
                  </CardContent></Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── MODAL CUPÓN ── */}
      <Dialog open={showCouponModal} onOpenChange={setShowCouponModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-orange-500" /> Crear Cupón de Descuento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Código del cupón *</Label>
              <Input value={couponForm.code} onChange={e => setCouponForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="OKRESEVA20" className="font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo de descuento</Label>
                <Select value={couponForm.discount_type} onValueChange={v => setCouponForm(p => ({ ...p, discount_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Porcentaje (%)</SelectItem>
                    <SelectItem value="fixed">Monto fijo (MXN)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor *</Label>
                <Input type="number" value={couponForm.discount_value} onChange={e => setCouponForm(p => ({ ...p, discount_value: e.target.value }))}
                  placeholder={couponForm.discount_type === 'percent' ? '20' : '200'} />
              </div>
            </div>
            <div>
              <Label>Aplica a plan (opcional)</Label>
              <Select value={couponForm.applies_to_plan} onValueChange={v => setCouponForm(p => ({ ...p, applies_to_plan: v }))}>
                <SelectTrigger><SelectValue placeholder="Todos los planes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos los planes</SelectItem>
                  {PLANS.filter(p => p.price_mxn > 0).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Máximo de usos</Label>
                <Input type="number" value={couponForm.max_redemptions} onChange={e => setCouponForm(p => ({ ...p, max_redemptions: e.target.value }))} placeholder="Sin límite" />
              </div>
              <div>
                <Label>Expira</Label>
                <Input type="date" value={couponForm.expires_at} onChange={e => setCouponForm(p => ({ ...p, expires_at: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCouponModal(false)}>Cancelar</Button>
            <Button onClick={saveCoupon} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Crear cupón'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── MODAL CONNECT ── */}
      <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-purple-600" /> Conectar Restaurante via Stripe Connect</DialogTitle></DialogHeader>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-2">
            <p className="text-xs text-purple-700">El restaurante recibirá un link para completar el onboarding de Stripe Express. Una vez completado, sus pagos pasarán por la plataforma con el fee configurado.</p>
          </div>
          <div className="space-y-4">
            <div><Label>ID del restaurante *</Label><Input value={connectForm.restaurant_id} onChange={e => setConnectForm(p => ({ ...p, restaurant_id: e.target.value }))} placeholder="rest_12345" /></div>
            <div><Label>Nombre del restaurante *</Label><Input value={connectForm.restaurant_name} onChange={e => setConnectForm(p => ({ ...p, restaurant_name: e.target.value }))} /></div>
            <div><Label>Email del propietario *</Label><Input type="email" value={connectForm.email} onChange={e => setConnectForm(p => ({ ...p, email: e.target.value }))} /></div>
            <div><Label>País</Label>
              <Select value={connectForm.country} onValueChange={v => setConnectForm(p => ({ ...p, country: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MX">México</SelectItem>
                  <SelectItem value="US">Estados Unidos</SelectItem>
                  <SelectItem value="ES">España</SelectItem>
                  <SelectItem value="CO">Colombia</SelectItem>
                  <SelectItem value="AR">Argentina</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectModal(false)}>Cancelar</Button>
            <Button onClick={createConnect} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white gap-2">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4" /> Crear y abrir onboarding</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
